import { List, Map } from "immutable";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import {
  RELATIONSHIP_ENTITY_COLUMN_WIDTH,
  RELATIONSHIP_EVENT_COLUMN_PADDING_Y,
  RELATIONSHIP_EVENT_COLUMN_WIDTH,
  RELATIONSHIP_GROUP_LABEL_WIDTH,
  RELATIONSHIP_INTERSECTION_AGE_SIZE,
  RELATIONSHIP_INTERSECTION_ICON_SIZE,
  RELATIONSHIP_ROW_Y_PADDING,
  SPLIT_BUTTON_LABEL_PADDING_Y,
  SPLIT_BUTTON_LINE_HEIGHT
} from "../../constants";
import CheckboxStates from "../../constants/checkboxStates";
import { ADD_COLUMN_PREFIX } from "../../constants/idPrefixes";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { stringFromTimespan } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import sortFunctions from "../../modules/items/sortFunctions";
import { narrativeSelectors } from "../../modules/narrative";
import { relationshipSelectors } from "../../modules/relationships";
import { immutableRecords } from "../../types";
import { getRowsWithSuperId } from "../../utils/tableData";
import workViewSelectors from "../workView/selectors";

const getRowsWithItems = createCachedSelector(
  (_state, _viewId, items) => items,
  appStateSelectors.getViewExpandedItems,
  narrativeSelectors.getNarrativeTree,
  appStateSelectors.getRelationshipViewSortBy,
  typeSelectors.getSequenceTypeIds,
  // Calculate result
  (items, expandedItems, narrativeTree, sortBy, sequenceTypes) => {
    // Get all objects that match the desired types
    const filtered = items.filter(o => sequenceTypes.contains(o.type));
    const sortFunction = sortFunctions.sortFunctionForOption(sortBy);
    return getRowsWithSuperId(filtered, null, expandedItems, sortFunction);
  }
)(workViewSelectors.getViewId);

const getRows = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getItemsInGroupForView,
  getRowsWithItems
)(workViewSelectors.getViewId);

const getSelectedRows = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getSelectedItemsInGroupForView,
  getRowsWithItems
)(workViewSelectors.getViewId);

// Work out which entities are being displayed as columns
// Receives: state, viewId
const getColumns = createCachedSelector(
  // work out our column type
  itemSelectors.getItemsById,
  appStateSelectors.getViewRelationshipColumnIdsAsSet,
  itemSelectors.getColumnItemIds,
  // calculate result: (list of entities matching the type)
  (itemsById, visibleColumnIdSet, allColumnIds) =>
    allColumnIds.filter(colId => visibleColumnIdSet.has(colId)).map(colId => itemsById.get(colId))
)(workViewSelectors.getViewId);

// Work out which entities are being displayed as columns
// Receives: state, viewId
const getColumnGroups = createCachedSelector(
  // work out our column type
  getColumns,
  state => definitionSelectors.getEnabledDefinitions(state).types,
  appStateSelectors.getFontSize,
  appStateSelectors.getViewRelationshipTypeIds,
  // calculate result: (list of entities matching the type)
  (columns, types, fontSize, viewRelationshipTypeIds) => {
    const SCALED_ENTITY_COLUMN_WIDTH = scalePixelsToFontSize(RELATIONSHIP_ENTITY_COLUMN_WIDTH, fontSize);
    const SCALED_GROUP_LABEL_WIDTH = scalePixelsToFontSize(RELATIONSHIP_GROUP_LABEL_WIDTH, fontSize);

    let globalLeft = 0;

    const groupedColumns = columns.groupBy(c => c.type);

    const groupedColumnsWithTypes = viewRelationshipTypeIds.reduce((obj, typeId) => {
      return obj.set(typeId, groupedColumns.get(typeId) ? groupedColumns.get(typeId) : List());
    }, new Map());

    const groupedColumnsWithAddNode = groupedColumnsWithTypes
      .map((g, gId) => {
        const t = types.byId.get(gId);
        const labelDisplay = typeSelectors.getTypeLabelDisplay(t);
        let count = g.count();
        let cols = g.map(c => immutableRecords.RelationshipColumnDisplayRecord(c));
        //#!if readonly !== true
// const addNodeRecord = immutableRecords.RelationshipColumnDisplayRecord({
// id: `${ADD_COLUMN_PREFIX}-${gId}`,
// label: `Create ${t.label}`,
// color: null,
// type: gId,
// icon: "sidebarAdd",
// left: null,
// width: null,
// selected: false
// });
// count += 1;
// cols = cols.push(addNodeRecord);
        //#!endif
        return immutableRecords.RelationshipColumnGroupDisplayRecord({
          id: t.id,
          label: t.label,
          color: t.color,
          icon: t.icon,
          labelDisplay: labelDisplay,
          cols: cols,
          width: count * SCALED_ENTITY_COLUMN_WIDTH + SCALED_GROUP_LABEL_WIDTH
        });
      })
      .toList()
      .sortBy(g => types.allIds.indexOf(g.id))
      .map(g => {
        const left = globalLeft;
        globalLeft += g.width;
        return g.set("left", left).update("cols", cols =>
          cols.map((c, i) =>
            c.merge({
              left: left + i * SCALED_ENTITY_COLUMN_WIDTH,
              width: SCALED_ENTITY_COLUMN_WIDTH
            })
          )
        );
      });

    // if (groupedColumnsWithAddNode.count() === 0) {
    //   // No columns, so create a fake one
    //   return List([
    //     immutableRecords.RelationshipColumnGroupDisplayRecord({
    //       id: "chooseColumn",
    //       label: "Choose Columns",
    //       cols: List(),
    //       width: SCALED_GROUP_LABEL_WIDTH
    //     })
    //   ]);
    // }
    return groupedColumnsWithAddNode;
  }
)(workViewSelectors.getViewId);

// Calculates the total Relationship Table Width
// Receives: state, viewId
const getTableWidth = createCachedSelector(
  // fetch parameters for last calculate function
  getColumnGroups,
  appStateSelectors.getFontSize,
  // Calculate result
  (colGroups, fontSize) => {
    const SCALED_EVENT_COLUMN_WIDTH = scalePixelsToFontSize(RELATIONSHIP_EVENT_COLUMN_WIDTH, fontSize);
    const totalGroupWidth = colGroups.reduce((sum, g) => (sum += g.width), 0);
    return totalGroupWidth + SCALED_EVENT_COLUMN_WIDTH;
  }
)(workViewSelectors.getViewId);

const getTableDataWithRows = createCachedSelector(
  // fetch parameters for last calculate function
  state => state.data.items.byId,
  state => state.data.relationships.byId,
  state => definitionSelectors.getReferencesById(state),
  (_state, _viewId, rows) => rows,
  getColumnGroups,
  appStateSelectors.getViewExpandedItems,
  getTableWidth,
  state => calendarSelectors.getCalendar(state),
  state => definitionSelectors.getRelationshipViewSettings(state),
  // Calculate result
  (
    itemsById,
    relationshipsById,
    referencesById,
    rows,
    colGroups,
    expandedItems,
    tableWidth,
    calendar,
    relationshipViewSettings
  ) => {
    const colGroupIds = colGroups.map(g => g.cols.map(col => col.get("id")));
    const colIdSet = colGroupIds.flatten().toSet();
    const width = tableWidth;

    const titleMetricsKey = "nestedTitleMetrics";

    let top = 0;

    const mergedRows = rows.map((itemDisplay, index) => {
      // Get relevant relationships for row.
      const rels = relationshipSelectors.getSubjectRelationshipsWithItems(
        itemsById,
        relationshipsById,
        itemDisplay.id,
        colIdSet
      );
      const inverseRels = relationshipSelectors.getSubjectInverseRelationshipsWithItems(
        itemsById,
        relationshipsById,
        itemDisplay.id,
        colIdSet
      );
      const relData = rels.map(rel => {
        // const itemIndex = findGroupIndexes(colGroupIds, rel.object);
        const color = definitionSelectors.getReferenceColor(referencesById, rel.reference);
        const res = immutableRecords.RelationshipEventColumnDataDisplayRecord({
          groupColumnIndex: rel.object,
          referenceId: rel.reference,
          label: definitionSelectors.getReferenceLabel(referencesById, rel.reference),
          icon: definitionSelectors.getReferenceIcon(referencesById, rel.reference),
          color
        });
        return res;
      });
      const inverseRelData = inverseRels.map(rel => {
        // const itemIndex = findGroupIndexes(colGroupIds, rel.subject);
        const color = definitionSelectors.getReferenceColor(referencesById, rel.reference);
        return immutableRecords.RelationshipEventColumnDataDisplayRecord({
          groupColumnIndex: rel.subject,
          referenceId: rel.reference,
          label: definitionSelectors.getReferenceInverseLabel(referencesById, rel.reference),
          icon: definitionSelectors.getReferenceIcon(referencesById, rel.reference),
          color
        });
      });

      const columnData = relData.concat(inverseRelData).groupBy(r => r.groupColumnIndex);
      const maxIconsPerColumn = columnData.reduce((m, list) => Math.max(m, list.count()), 0);

      let ageData = {};
      let ageHeight = 0;
      if (relationshipViewSettings.showDates) {
        rels.map(rel => {
          // const itemIndex = findGroupIndexes(colGroupIds, rel.object);
          const item = itemsById.get(rel.object);

          if (item.startDate && item.startDate.timestamp && itemDisplay.startDate && itemDisplay.startDate.timestamp) {
            const endDate = dateByAddingSpan(calendar, item.startDate, item.duration);
            // Only calculate age if it's >= the item's start date and <= the item's end date
            if (
              (!endDate.timestamp ||
                endDate.timestamp >= itemDisplay.startDate.timestamp ||
                endDate.timestamp === item.startDate.timestamp) &&
              item.startDate.timestamp <= itemDisplay.startDate.timestamp
            ) {
              const ageSpan = dateSubtractionFunctions.timespanBetweenDates(
                calendar,
                item.startDate,
                itemDisplay.startDate
              );
              const objectAge = stringFromTimespan(ageSpan, 2, true);
              ageData[rel.object] = objectAge;
              ageHeight = RELATIONSHIP_INTERSECTION_AGE_SIZE;
            }
          }
        });
      }

      const columnAgeData = Map(ageData);
      const iconsHeight = Math.ceil(maxIconsPerColumn / 2) * RELATIONSHIP_INTERSECTION_ICON_SIZE;
      const height =
        Math.max(itemDisplay.itemCardSize.height, iconsHeight + ageHeight) + 2 * RELATIONSHIP_ROW_Y_PADDING;

      const rect = immutableRecords.FlatRectRecord({
        top,
        height,
        left: 0,
        width: width,
        right: width,
        bottom: top + height
      });
      top += height;

      return immutableRecords.RelationshipItemDisplayRecord({
        itemDisplay: itemDisplay.set("index", index),
        columnData,
        rect,
        indentationLevel: itemDisplay.superDepth,
        hasChildren: itemDisplay.children.size > 0,
        childrenExpanded: expandedItems.contains(itemDisplay.id),
        columnAgeData
      });
    });
    return immutableRecords.RelationshipViewDisplayRecord({
      rows: mergedRows,
      colGroups,
      width: width,
      height: top,
      titleMetricsKey
    });
  }
)(workViewSelectors.getViewId);

// Merges the entity and event details to work out the table data.
// Receives: state, viewId
const getTableData = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  getRows,
  getTableDataWithRows
)(workViewSelectors.getViewId);

const getSelectedTableData = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  getSelectedRows,
  getTableDataWithRows
)(workViewSelectors.getViewId);

const getAddRowHeight = fontSize => {
  const splitButtonLineHeight = scalePixelsToFontSize(SPLIT_BUTTON_LINE_HEIGHT, fontSize);
  const splitButtonLabelPaddingY = scalePixelsToFontSize(SPLIT_BUTTON_LABEL_PADDING_Y, fontSize);
  const relationshipEventColumnPaddingY = scalePixelsToFontSize(RELATIONSHIP_EVENT_COLUMN_PADDING_Y, fontSize);
  return splitButtonLineHeight + splitButtonLabelPaddingY * 2 + relationshipEventColumnPaddingY * 2;
};

const getRelationshipViewDataWithTableData = createCachedSelector(
  (_state, _viewId, data) => data,
  definitionSelectors.getColorMap,
  appStateSelectors.getFontSize,
  (data, colorMap, fontSize) => {
    return data.withMutations(d => {
      d.update("colGroups", groups =>
        groups.map(g =>
          g.update("cols", cols => cols.map(c => c.set("color", itemSelectors.getItemSwatchColor(colorMap, c))))
        )
      );
      const addRowHeight = getAddRowHeight(fontSize);
      d.update("height", h => h + addRowHeight);
    });
  }
)(workViewSelectors.getViewId);

// Merges the event layout with the current selection.
// Receives: state, viewId
const getRelationshipViewData = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  getTableData,
  getRelationshipViewDataWithTableData
)(workViewSelectors.getViewId);

const getRelationshipViewSelectedData = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  getSelectedTableData,
  getRelationshipViewDataWithTableData
)(workViewSelectors.getViewId);

const getRelationshipViewSortedItemIds = createCachedSelector(
  // fetch parameters for last calculate function
  getRows,
  // Calculate result
  rows => {
    return rows.map(r => r.id);
  }
)(workViewSelectors.getViewId);

const getAllowedReferencesPerSubjectObjectTypes = createSelector(
  // fetch parameters for last calculate function
  state => definitionSelectors.getTypesById(state),
  state => definitionSelectors.getReferencesById(state),
  // And finally call it with this
  (types, references) => {
    // Iterate through all types as if they are the subject
    return types.map(subjectType => {
      const subjectRefs = subjectType.references;

      // Iterate through all types to pair as items for this type
      return types.map(itemType => {
        // Iterate through all the references of the subject
        return (
          subjectRefs
            .map(refId => {
              const ref = references.get(refId);
              // If it includes the item type, return it... otherwise return null
              return ref.types.includes(itemType.id) ? ref : null;
            })
            // And finally filter all the nulls out of the list
            .filter(obj => obj !== null)
        );
      });
    });
  }
);

const getAllowedInverseReferencesPerSubjectObjectTypes = createSelector(
  // fetch parameters for last calculate function
  state => definitionSelectors.getTypesById(state),
  state => definitionSelectors.getReferencesById(state),
  // And finally call it with this
  (types, references) => {
    // Iterate through all types as if they are the subject
    return types.map(subjectType => {
      // Iterate through all types to pair as items for this type
      return types.map(itemType => {
        const itemRefs = itemType.references;

        // Iterate through all the inverse references of the subject
        return (
          itemRefs
            .map(refId => {
              const ref = references.get(refId);
              // If it includes the type, return it... otherwise return null
              return ref.types.includes(subjectType.id) ? ref : null;
            })
            // And finally filter all the nulls out of the list
            .filter(obj => obj !== null)
        );
      });
    });
  }
);

const getAllowedColumnGroups = createSelector(
  // fetch parameters for last calculate function
  itemSelectors.getColumnItemIdsByType,
  definitionSelectors.getTypesById,
  itemSelectors.getItemsById,
  // Calculate the final result
  (columnIdsByTypes, typesById, itemsById) =>
    columnIdsByTypes
      .map((colIds, tId) => {
        const typeObj = typesById.get(tId);
        const groupOptions = colIds.map(colId => {
          const o = itemsById.get(colId);
          return immutableRecords.DropdownOptionRecord({
            value: o.id,
            label: o.label
          });
        });

        const allowAdd = false;

        return immutableRecords.DropdownGroupRecord({
          value: typeObj.id,
          label: typeObj.label,
          children: groupOptions,
          allowAdd
        });
      })
      .toList()
);

const getAllowedColumnGroupsForView = createCachedSelector(
  // inputs
  getAllowedColumnGroups,
  appStateSelectors.getViewRelationshipColumnIds,
  appStateSelectors.getViewRelationshipTypeIds,
  // output
  (columnGroups, selectedColumns, viewRelationshipTypeIds) => {
    return columnGroups.map(cg => {
      return cg.withMutations(g => {
        let foundChecked = false;
        let foundUnchecked = false;
        g.update("children", children =>
          children.map(c => {
            const selected = selectedColumns.contains(c.value);
            if (selected) {
              foundChecked = true;
            } else {
              foundUnchecked = true;
            }
            return c.set("selected", selected);
          })
        );
        const showType = viewRelationshipTypeIds.includes(g.value);
        const state = showType
          ? foundChecked && foundUnchecked
            ? CheckboxStates.MIXED
            : CheckboxStates.CHECKED
          : CheckboxStates.UNCHECKED;
        g.set("state", state);
      });
    });
  }
)(workViewSelectors.getViewId);

const doesRelationshipViewHaveData = (state, viewId) => {
  const data = getRelationshipViewData(state, viewId);
  const columnItems = itemSelectors.getColumnItemIds(state);
  const viewHasData = data.rows.count() > 0 && columnItems.count() > 0;
  return viewHasData;
};

const getTypes = createSelector(
  // inputs
  state => definitionSelectors.getEnabledDefinitions(state).types.allIds,
  state => definitionSelectors.getEnabledDefinitions(state).types.byId,
  // output
  (allIds, typesById) => allIds.map(tId => typesById.get(tId))
);

const getRelationshipViewDefaultType = state => definitionSelectors.getDefaultSequenceType(state);

export default {
  getRelationshipViewData,
  getRelationshipViewSelectedData,
  getRelationshipViewSortedItemIds,
  getAllowedReferencesPerSubjectObjectTypes,
  getAllowedInverseReferencesPerSubjectObjectTypes,
  getAllowedColumnGroupsForView,
  doesRelationshipViewHaveData,
  getTypes,
  getTableData,
  getTableWidth,
  getRelationshipViewDefaultType
};
